import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CompassOutlined, HomeOutlined } from '@ant-design/icons';
import styles from './PagesHeader.module.css';
import logo from '../assets/HeatCraftHPLogoTransparent.png';
import LanguageSwitcher from '../components/LanguageSwitcher';
import UserMenu from '../components/UserMenu';
import NavigationMenu from '../components/NavigationMenu';
import NavigationAssistant from '../components/NavigationAssistant';

/**
 * Header component with logo and optional navigation
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS class name
 * @param {boolean} [props.clickableLogo=true] - Whether the logo is clickable
 * @param {string} [props.logoAlt="HeatCraft HP Logo"] - Alt text for the logo
 * @returns {JSX.Element} The header component
 */
const PagesHeader = ({ 
  className = '', 
  clickableLogo = true,
  logoAlt = "HeatCraft HP Logo" 
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [assistantOpen, setAssistantOpen] = useState(false);

  const handleLogoClick = useCallback(() => {
    if (clickableLogo) {
      navigate('/');
    }
  }, [clickableLogo, navigate]);

  return (
    <header className={`${styles.header} ${className}`.trim()}>
      <div className={styles.headerContent}>
        {/* Navigation Buttons */}
        <div className={styles.navButtons}>
          <NavigationMenu />
          {/*<button
            className={styles.navButton}
            onClick={() => setAssistantOpen(true)}
            aria-label={t('assistant.openButton')}
            title={t('assistant.openButton')}
          >
            <CompassOutlined className={styles.navIcon} />
          </button>*/}
          <button 
            className={styles.navButton}
            onClick={() => navigate('/')}
            aria-label={t('nav.home')}
            title={t('nav.home')}
          >
            <HomeOutlined className={styles.navIcon} />
          </button>
        </div>
        <div className={styles.rightContent}>
          <img 
            src={logo} 
            alt={logoAlt} 
            className={`${styles.logo} ${clickableLogo ? styles.clickable : ''}`}
            onClick={clickableLogo ? handleLogoClick : undefined}
            draggable="false"
            role={clickableLogo ? "button" : undefined}
            tabIndex={clickableLogo ? 0 : undefined}
            onKeyDown={(e) => clickableLogo && e.key === 'Enter' && handleLogoClick()}
          />
          <LanguageSwitcher />
          <div className={styles.headerDivider}></div>
          <UserMenu />
        </div>
        <NavigationAssistant open={assistantOpen} onClose={() => setAssistantOpen(false)} />
      </div>
    </header>
  );
};

PagesHeader.propTypes = {
  className: PropTypes.string,
  clickableLogo: PropTypes.bool,
  logoAlt: PropTypes.string,
};

export default React.memo(PagesHeader);